import { addItemToCartMutation } from '@/api/carts/cartMutations';
import { itemsQuery } from '@/api/items/itemQueries';
import { Button } from '@/components/common/Button';
import { Combobox } from '@/components/common/Combobox';
import { Select } from '@/components/common/Select';
import { useFormMutation } from '@/hooks/useFormMutation';
import { addItemToCartSchema } from '@/validation/cartValidator';
import { useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import { toFormikValidate } from 'zod-formik-adapter';

interface CartAddItemViewProps {
  cartId: number;
  onClose: () => void;
}

export interface AddItemFormValues {
  itemId: string;
  strengthId: string;
}

export const CartAddItemView = ({ cartId, onClose }: CartAddItemViewProps) => {
  const { data: itemsData } = useQuery(itemsQuery());
  const [step, setStep] = useState(0);

  const formik = useFormik<AddItemFormValues>({
    initialValues: {
      itemId: '',
      strengthId: '',
    },
    validate: toFormikValidate(addItemToCartSchema),
    onSubmit: async () => {
      await handleFinish();
    },
  });

  const addItemToCart = useFormMutation({
    ...addItemToCartMutation(cartId),
    formik,
  });

  const selectedItem = useMemo(() => {
    if (!formik.values.itemId) return;

    return itemsData?.data.find(
      (item) => item.id.toString() === formik.values.itemId,
    );
  }, [itemsData, formik.values.itemId]);

  const validateFields = async (fields: Array<keyof AddItemFormValues>) => {
    fields.forEach((field) => formik.setFieldTouched(field, true));
    const errors = await formik.validateForm();
    return !fields.some((field) => errors[field]);
  };

  const handleNext = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();

    if (await validateFields(['itemId'])) {
      setStep(1);
    }
  };

  const handleFinish = async () => {
    if (await validateFields(['strengthId'])) {
      await addItemToCart.mutateAsync({
        price_id: Number(formik.values.strengthId),
        quantity: 1,
      });

      formik.resetForm();
      setStep(0);
      onClose();
    }
  };

  const handleBack = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();

    if (step === 1) {
      setStep(0);
      formik.setFieldValue('strengthId', '', false);
      formik.setFieldTouched('strengthId', false);
    } else {
      formik.resetForm();
      setStep(0);
      onClose();
    }
  };

  const comboboxItems =
    itemsData?.data.map((item) => ({
      id: item.id,
      value: item.name,
      label: item.name,
    })) || [];

  return (
    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-2.5">
      {step === 0 ? (
        <Combobox
          id="supplement-search"
          label="Search supplement"
          items={comboboxItems}
          value={formik.values.itemId}
          onChange={async (value) => {
            await formik.setFieldValue('itemId', value || '', true);
            if (value && value !== formik.values.itemId) {
              formik.setFieldValue('strengthId', '');
            }
          }}
          onBlur={() => {
            formik.setFieldTouched('itemId', true);
          }}
          error={
            formik.touched.itemId && formik.errors.itemId
              ? formik.errors.itemId
              : undefined
          }
          touched={formik.touched.itemId}
        />
      ) : (
        <Select
          id="strength-select"
          label={`${selectedItem?.name} dose:`}
          options={
            selectedItem?.strengths?.map((strength) => ({
              label: strength.strength,
              value: String(strength.id),
            })) || []
          }
          value={formik.values.strengthId}
          onChange={(value) => {
            formik.setFieldValue('strengthId', value);
          }}
          onBlur={formik.handleBlur}
          error={
            formik.touched.strengthId && formik.errors.strengthId
              ? formik.errors.strengthId
              : undefined
          }
          touched={formik.touched.strengthId}
        />
      )}

      <div className="grid grid-cols-2 gap-2.5">
        <Button
          type={step === 1 ? 'submit' : 'button'}
          onClick={step === 0 ? handleNext : undefined}
          disabled={addItemToCart.isPending}
        >
          {step === 0 ? 'Add' : 'Add supplement'}
        </Button>

        <Button type="button" onClick={handleBack} variant="outlinePrimary">
          Back
        </Button>
      </div>
    </form>
  );
};
